import React, { useState } from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import { useClaim } from '~/components/ClaimContainer';
import { getAllSearchableContactRoles } from '~/components/communications/ContactUtils';
import { ConfirmModal } from '~/components/ConfirmModal';
import ContactTextFieldFormik from '~/components/ContactTextFieldFormik';
import Button from '~/components/core/Atomic/Buttons/Button';
import CollapsibleWrapper from '~/components/core/Collapsible/CollapsibleWrapper';
import EmptyState from '~/components/core/EmptyState';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import SortableTable from '~/components/core/Tables/SortableTable';
import Heading from '~/components/core/TextComponents/Heading';
import Text from '~/components/core/TextComponents/Text';
import ThreeDotsMenu from '~/components/core/ThreeDotsMenu';
import DocumentLink from '~/components/Documents/DocumentLink';
import DocumentTextFieldFormik from '~/components/Documents/DocumentTextFieldFormik';
import { AddIcon, MedicalCrossIcon } from '~/components/icons';
import useOrganization from '~/components/OrganizationContext';
import { DatePickerTextFieldFormik, TextFieldFormik } from '~/components/TextFieldFormik';
import useDataFetcher from '~/components/useDataFetcher';
import { isoDateToUs } from '~/DateTimeUtils';
import { reportAxiosError, stringCmp } from '~/Utils';

import {
  createMedicalRecord,
  deleteMedicalRecord,
  getMedicalRecordsUrl,
  setMedicalDocumentNotification,
  updateMedicalRecord,
} from './apiService';
import IncomeProtectionNotification from './IncomeProtectionNotification';
import type {
  IncomeProtectionMedicalRecordsResponse,
  MedicalRecordCreationType,
  MedicalRecordType as MedicalRecord,
  MedicalRecordUpdateType,
  MinimalNotificationType,
} from './types';

const TEXTS = {
  sectionTitle: 'Medical Records',
  addDialogTitle: 'Add Medical Record',
  editDialogTitle: 'Edit Medical Record',
  documentLabel: 'Medical Record Document',
  diagnosesLabel: 'Diagnoses',
  practitionerLabel: 'Medical Practitioner',
  certificateStartLabel: 'Certificate Start',
  certificateEndLabel: 'Certificate End',
  rtwDateLabel: 'RTW Date',
  cancel: 'Cancel',
  add: 'Add',
  save: 'Save',
  required: 'Required',
  endDateBeforeStart: 'Certificate End must be on or after Certificate Start',
  emptyState: 'No medical records',
  tableColumns: {
    documentDate: 'Document Date',
    diagnoses: 'Diagnoses',
    document: 'Document',
    practitioner: 'Medical Practitioner',
    certificateStart: 'Certificate Start',
    certificateEnd: 'Certificate End',
    rtwDate: 'RTW Date',
    actions: 'Actions',
  },
  editAction: 'Edit',
  deleteAction: 'Delete',
  deleteConfirmTitle: 'Delete Medical Record',
  deleteConfirmMessage: 'Are you sure you want to delete this medical record? This action cannot be undone.',
  deleteConfirmButton: 'Delete',
  deleteCancelButton: 'Cancel',
  setNotificationDialogTitle: 'Set Medical Certificate Notification',
  setNotificationDialogContent: 'Set a notification to remind you when the medical certificate expires.',
  latestCertificationDetails: (
    documentName: string,
    certificateStart: string,
    certificateEnd: string,
    rtwDate: string | null
  ) => `Latest cert end: ${documentName}, Period: ${certificateStart} - ${certificateEnd}, RTW: ${rtwDate ?? 'N/A'}`,
} as const;

type MedicalRecordFormValues = {
  document_id: number | '';
  diagnoses: string;
  medical_practitioner_contact_id: number | null;
  medical_practitioner_contact: { id: number } | null;
  medical_practitioner_contact_full_name: string | null;
  certificate_start: string | null;
  certificate_end: string | null;
  rtw_date: string | null;
};

const createValidationSchema = Yup.object().shape({
  document_id: Yup.number().required(TEXTS.required),
  diagnoses: Yup.string().required(TEXTS.required),
  medical_practitioner_contact_id: Yup.number().nullable(),
  certificate_start: Yup.date().nullable().required(TEXTS.required),
  certificate_end: Yup.date()
    .nullable()
    .required(TEXTS.required)
    .min(Yup.ref('certificate_start'), TEXTS.endDateBeforeStart),
  rtw_date: Yup.date().nullable(),
});

const editValidationSchema = Yup.object().shape({
  diagnoses: Yup.string().required(TEXTS.required),
  medical_practitioner_contact_id: Yup.number().nullable(),
  certificate_start: Yup.date().nullable().required(TEXTS.required),
  certificate_end: Yup.date()
    .nullable()
    .required(TEXTS.required)
    .min(Yup.ref('certificate_start'), TEXTS.endDateBeforeStart),
  rtw_date: Yup.date().nullable(),
});

const initialValues: MedicalRecordFormValues = {
  document_id: '',
  diagnoses: '',
  medical_practitioner_contact_id: null,
  medical_practitioner_contact: null,
  medical_practitioner_contact_full_name: '',
  certificate_start: null,
  certificate_end: null,
  rtw_date: null,
};

interface SectionTitleProps {
  records: MedicalRecord[];
}

const SectionTitle = ({ records }: SectionTitleProps) => {
  const count = records.length;

  // Find the record with the furthest certificate_end date
  const latestRecord =
    count > 0
      ? records.reduce((latest, record) =>
          new Date(record.certificate_end) > new Date(latest.certificate_end) ? record : latest
        )
      : null;

  return (
    <div className="flex items-center gap-12">
      <div className="me-4 inline-flex rounded-md bg-green-100 p-[10px] text-green-700">
        <MedicalCrossIcon iconColor="currentColor" size={24} />
      </div>
      <div className="flex flex-col">
        <Text variant={Text.VARIANTS.SM}>
          {TEXTS.sectionTitle}
          {count > 0 ? ` (${count})` : ''}
        </Text>
        {latestRecord && latestRecord?.document_name && (
          <Text variant={Text.VARIANTS.XS}>
            {TEXTS.latestCertificationDetails(
              latestRecord.document_name,
              isoDateToUs(latestRecord.certificate_start),
              isoDateToUs(latestRecord.certificate_end),
              latestRecord.rtw_date ? isoDateToUs(latestRecord.rtw_date) : null
            )}
          </Text>
        )}
      </div>
    </div>
  );
};

const MedicalRecordFormInner = ({ onCancel, isEditMode }: { onCancel: () => void; isEditMode?: boolean }) => {
  const { isSubmitting } = useFormikContext<MedicalRecordFormValues>();
  // @ts-expect-error - useOrganization is not typed
  const { organizationContactRolesDict } = useOrganization();

  return (
    <Form className="grid gap-24">
      <div className="grid gap-16">
        <DocumentTextFieldFormik id="document_id" label={TEXTS.documentLabel} showOnly={isEditMode} />
        <TextFieldFormik id="diagnoses" label={TEXTS.diagnosesLabel} />
        <ContactTextFieldFormik
          id="medical_practitioner_contact"
          label={TEXTS.practitionerLabel}
          acceptedRoles={getAllSearchableContactRoles(organizationContactRolesDict)}
          fixedSearchResults
          nullIfEmpty
        />
      </div>
      <div className="grid grid-cols-3 gap-16">
        <DatePickerTextFieldFormik
          id="certificate_start"
          label={TEXTS.certificateStartLabel}
          className="w-full"
          clearable
        />
        <DatePickerTextFieldFormik
          id="certificate_end"
          label={TEXTS.certificateEndLabel}
          className="w-full"
          clearable
        />
        <DatePickerTextFieldFormik id="rtw_date" label={TEXTS.rtwDateLabel} className="w-full" clearable nullIfEmpty />
      </div>
      <div className="flex justify-end gap-16">
        <Button type="button" variant="outlined" color="primary" onClick={onCancel} disabled={isSubmitting}>
          {TEXTS.cancel}
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
          {isEditMode ? TEXTS.save : TEXTS.add}
        </Button>
      </div>
    </Form>
  );
};

interface MedicalRecordsSectionProps {
  organizationId: number;
  claimId: number;
  exposureId: number;
  onMedicalRecordsChanged?: () => void;
  viewOnly?: boolean;
  latestCertificateEndDate?: string | null;
  medicalDocumentNotification?: MinimalNotificationType | null;
  isMedicalDocumentNotificationEnabled: boolean;
}

const MedicalRecordsSection = ({
  organizationId,
  claimId,
  exposureId,
  onMedicalRecordsChanged,
  viewOnly = false,
  latestCertificateEndDate,
  medicalDocumentNotification,
  isMedicalDocumentNotificationEnabled,
}: MedicalRecordsSectionProps) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<MedicalRecord | null>(null);
  const [deletingRecord, setDeletingRecord] = useState<MedicalRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { claim } = useClaim();

  const handleNotificationUpdate = () => {
    onMedicalRecordsChanged?.();
  };

  const createMedicalDocumentNotification = (dueDate: string) => {
    return setMedicalDocumentNotification({
      organizationId,
      claimId,
      exposureId,
      dueDate,
    });
  };

  const closeAddDialog = () => setIsAddDialogOpen(false);
  const closeEditDialog = () => setEditingRecord(null);
  const closeDeleteConfirm = () => setDeletingRecord(null);

  const {
    isLoading,
    isError,
    data,
    reloadData: reloadMedicalRecords,
  } = useDataFetcher<IncomeProtectionMedicalRecordsResponse>(
    getMedicalRecordsUrl({ organizationId, claimId, exposureId })
  );

  const records = data?.medical_records ?? [];

  const getDocument = (documentId: number) => claim?.documents?.find((d: { id: number }) => d.id === documentId);

  const baseColumns = [
    {
      id: 'document_date',
      label: TEXTS.tableColumns.documentDate,
      disableSort: true,
      specialCell: (row: MedicalRecord) => (row.document_date ? isoDateToUs(row.document_date) : '-'),
    },
    {
      id: 'medical_practitioner_contact_full_name',
      disableSort: true,
      label: TEXTS.tableColumns.practitioner,
      specialCell: (row: MedicalRecord) => row.medical_practitioner_contact_full_name ?? '-',
    },
    {
      id: 'diagnoses',
      disableSort: true,
      label: TEXTS.tableColumns.diagnoses,
    },
    {
      id: 'certificate_start',
      label: TEXTS.tableColumns.certificateStart,
      specialCell: (row: MedicalRecord) => isoDateToUs(row.certificate_start),
      specialCmpFunc: (row1: MedicalRecord, row2: MedicalRecord) =>
        stringCmp(row1.certificate_start, row2.certificate_start),
    },
    {
      id: 'certificate_end',
      label: TEXTS.tableColumns.certificateEnd,
      specialCell: (row: MedicalRecord) => isoDateToUs(row.certificate_end),
      specialCmpFunc: (row1: MedicalRecord, row2: MedicalRecord) =>
        stringCmp(row1.certificate_end, row2.certificate_end),
    },
    {
      id: 'rtw_date',
      label: TEXTS.tableColumns.rtwDate,
      specialCell: (row: MedicalRecord) => (row.rtw_date ? isoDateToUs(row.rtw_date) : '-'),
      specialCmpFunc: (row1: MedicalRecord, row2: MedicalRecord) => {
        if (!row1.rtw_date && !row2.rtw_date) {
          return 0;
        }
        if (!row1.rtw_date) {
          return 1;
        }
        if (!row2.rtw_date) {
          return -1;
        }
        return stringCmp(row1.rtw_date, row2.rtw_date);
      },
    },
    {
      id: 'document_name',
      label: TEXTS.tableColumns.document,
      disableSort: true,
      specialCell: (row: MedicalRecord) => {
        const document = getDocument(row.document_id);
        return document?.document_name ? <DocumentLink document={document} text={document.document_name} /> : null;
      },
    },
  ];

  const actionsColumn = {
    id: 'actions',
    label: TEXTS.tableColumns.actions,
    disableSort: true,
    specialCell: (row: MedicalRecord) => (
      <ThreeDotsMenu
        options={[
          { key: 'edit', node: TEXTS.editAction, onClick: () => setEditingRecord(row) },
          { key: 'delete', node: TEXTS.deleteAction, onClick: () => setDeletingRecord(row) },
        ]}
      />
    ),
  };

  const tableColumns = viewOnly ? baseColumns : [...baseColumns, actionsColumn];

  const handleDeleteConfirm = async () => {
    if (!deletingRecord) return;

    setIsDeleting(true);
    try {
      await deleteMedicalRecord({
        organizationId,
        claimId,
        exposureId,
        recordId: deletingRecord.id,
      });
      await reloadMedicalRecords();
      onMedicalRecordsChanged?.();
      closeDeleteConfirm();
    } catch (error) {
      reportAxiosError(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateSubmit = async (values: MedicalRecordFormValues) => {
    const data: MedicalRecordCreationType = {
      document_id: values.document_id as number,
      diagnoses: values.diagnoses,
      medical_practitioner_contact_id: values.medical_practitioner_contact_id,
      certificate_start: values.certificate_start as string,
      certificate_end: values.certificate_end as string,
      rtw_date: values.rtw_date,
    };

    try {
      await createMedicalRecord({ organizationId, claimId, exposureId, data });
      await reloadMedicalRecords();
      onMedicalRecordsChanged?.();
      closeAddDialog();
    } catch (error) {
      reportAxiosError(error);
    }
  };

  const handleEditSubmit = async (values: MedicalRecordFormValues) => {
    if (!editingRecord) return;

    const data: MedicalRecordUpdateType = {
      diagnoses: values.diagnoses,
      medical_practitioner_contact_id: values.medical_practitioner_contact_id,
      certificate_start: values.certificate_start as string,
      certificate_end: values.certificate_end as string,
      rtw_date: values.rtw_date,
    };

    try {
      await updateMedicalRecord({
        organizationId,
        claimId,
        exposureId,
        recordId: editingRecord.id,
        data,
      });
      await reloadMedicalRecords();
      onMedicalRecordsChanged?.();
      closeEditDialog();
    } catch (error) {
      reportAxiosError(error);
    }
  };

  const getEditInitialValues = (record: MedicalRecord): MedicalRecordFormValues => ({
    document_id: record.document_id,
    diagnoses: record.diagnoses,
    medical_practitioner_contact_id: record.medical_practitioner_contact_id ?? null,
    medical_practitioner_contact: record.medical_practitioner_contact_id
      ? { id: record.medical_practitioner_contact_id }
      : null,
    medical_practitioner_contact_full_name: record.medical_practitioner_contact_full_name ?? null,
    certificate_start: record.certificate_start,
    certificate_end: record.certificate_end,
    rtw_date: record.rtw_date ?? null,
  });

  return (
    <div className="rounded-xl border-1 border-solid border-slate-500 [&>*]:m-0 [&>*]:p-24">
      <CollapsibleWrapper
        title={<SectionTitle records={records} />}
        actionCard
        noBackground
        noBorderLine
        summarySectionClassname="items-center"
        chevronContainerClassName="!mt-0"
        withActionsContainerFullWidth={false}
        actions={
          viewOnly ? null : (
            <div className="ms-16 flex items-center gap-16" onClick={(e) => e.stopPropagation()}>
              <IncomeProtectionNotification
                existingNotification={medicalDocumentNotification}
                isNotificationTypeEnabled={isMedicalDocumentNotificationEnabled}
                createNotification={createMedicalDocumentNotification}
                onNotificationUpdate={handleNotificationUpdate}
                notificationCreateDialogTitle={TEXTS.setNotificationDialogTitle}
                notificationCreateDialogSubtitle={TEXTS.setNotificationDialogContent}
                defaultDueDateForNewNotification={latestCertificateEndDate}
              />
              <Button
                color="primary"
                variant="outlined"
                onClick={() => {
                  setIsAddDialogOpen(true);
                }}
                startIcon={<AddIcon iconColor="currentColor" />}
              >
                {TEXTS.add}
              </Button>
            </div>
          )
        }
      >
        {isLoading || isError ? (
          <SkeletonTable rowsCount={1} columnsCount={tableColumns.length} isError={isError} />
        ) : (
          <SortableTable
            columns={tableColumns}
            rows={records}
            autoPaginateRowsPerPage={5}
            emptyStateComponent={<EmptyState title={TEXTS.emptyState} />}
          />
        )}
      </CollapsibleWrapper>

      {isAddDialogOpen ? (
        <CardDialog
          isDialog
          open
          onClose={closeAddDialog}
          trackAlt="Add Medical Record Dialog"
          fullWidth
          classes={{ paper: 'min-w-[640px]' }}
          title={<Heading variant={Heading.TYPES.H1}>{TEXTS.addDialogTitle}</Heading>}
        >
          <Formik initialValues={initialValues} validationSchema={createValidationSchema} onSubmit={handleCreateSubmit}>
            <MedicalRecordFormInner onCancel={closeAddDialog} />
          </Formik>
        </CardDialog>
      ) : null}

      {editingRecord ? (
        <CardDialog
          isDialog
          open
          onClose={closeEditDialog}
          trackAlt="Edit Medical Record Dialog"
          fullWidth
          classes={{ paper: 'min-w-[640px]' }}
          title={<Heading variant={Heading.TYPES.H1}>{TEXTS.editDialogTitle}</Heading>}
        >
          <Formik
            initialValues={getEditInitialValues(editingRecord)}
            validationSchema={editValidationSchema}
            onSubmit={handleEditSubmit}
          >
            <MedicalRecordFormInner onCancel={closeEditDialog} isEditMode />
          </Formik>
        </CardDialog>
      ) : null}

      <ConfirmModal
        isOpen={!!deletingRecord}
        onClose={closeDeleteConfirm}
        title={TEXTS.deleteConfirmTitle}
        contentText={TEXTS.deleteConfirmMessage}
        primaryButtonName={TEXTS.deleteConfirmButton}
        secondaryButtonName={TEXTS.deleteCancelButton}
        onPrimaryBtnClick={handleDeleteConfirm}
        isSubmitting={isDeleting}
      />
    </div>
  );
};

export default MedicalRecordsSection;
